import { usePageTitle } from "@/hooks/use-page-title";
import { parseDateOnly } from "@/lib/dateUtils";
import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Edit2, Trash2, DollarSign, Calendar, Search, Building2, X, Filter } from "lucide-react";
import ResponsiveLayout from "@/components/layout/responsive-layout";
import { useToast } from "@/hooks/use-toast";
import { CreateServiceRateModal } from "@/components/modals/create-service-rate-modal";
import { EditServiceRateModal } from "@/components/modals/edit-service-rate-modal";
import { CreateCompanyServiceRateModal } from "@/components/modals/create-company-service-rate-modal";
import { EditCompanyServiceRateModal } from "@/components/modals/edit-company-service-rate-modal";
import { apiRequest } from "@/lib/queryClient";
import { formatDateForDisplay, fromDateOnlyString } from "@/lib/utils";
import type { ServiceRateWithRelations, ServiceType, CompanyServiceRateWithRelations } from "@shared/schema";

// Extended type to include derived status from the API
interface ServiceRateWithDerivedStatus extends ServiceRateWithRelations {
  derivedStatus?: 'active' | 'scheduled' | 'superseded';
}

export function ServiceRatesPage() {
  usePageTitle("Service Rates");
  const [activeTab, setActiveTab] = useState("standard");
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedServiceRate, setSelectedServiceRate] = useState<ServiceRateWithRelations | null>(null);
  const [createCustomModalOpen, setCreateCustomModalOpen] = useState(false);
  const [editCustomModalOpen, setEditCustomModalOpen] = useState(false);
  const [selectedCustomRate, setSelectedCustomRate] = useState<CompanyServiceRateWithRelations | null>(null);
  
  // Standard rates filters
  const [standardServiceTypeFilter, setStandardServiceTypeFilter] = useState<string>("all");
  const [standardStatusFilter, setStandardStatusFilter] = useState<string>("active");
  const [standardDateFilter, setStandardDateFilter] = useState<string>("");
  
  // Custom rates filters
  const [customServiceTypeFilter, setCustomServiceTypeFilter] = useState<string>("all");
  const [customStatusFilter, setCustomStatusFilter] = useState<string>("all");
  const [customCompanySearch, setCustomCompanySearch] = useState<string>("");
  const [customDateFilter, setCustomDateFilter] = useState<string>("");

  // Delete confirmation state
  const [deleteStandardId, setDeleteStandardId] = useState<number | null>(null);
  const [deleteCustomId, setDeleteCustomId] = useState<number | null>(null);
  
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: serviceRates = [], isLoading } = useQuery<ServiceRateWithDerivedStatus[]>({
    queryKey: ['/api/service-rates'],
    refetchOnMount: "always",
  });

  const { data: companyServiceRates = [], isLoading: isLoadingCustom } = useQuery<CompanyServiceRateWithRelations[]>({
    queryKey: ['/api/company-service-rates'],
    refetchOnMount: "always",
  });

  const { data: serviceTypes = [] } = useQuery<ServiceType[]>({
    queryKey: ['/api/service-types'],
    refetchOnMount: "always",
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => apiRequest('DELETE', `/api/service-rates/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/service-rates'] });
      toast({
        title: "Success",
        description: "Service rate deleted successfully",
      });
    },
    onError: (error: any) => {
      let description = "Failed to delete service rate";
      try {
        const raw = error?.message ?? "";
        const jsonStart = raw.indexOf("{");
        if (jsonStart !== -1) {
          const parsed = JSON.parse(raw.slice(jsonStart));
          if (parsed?.message) description = parsed.message;
        }
      } catch {}
      toast({
        title: "Error",
        description,
        variant: "destructive",
      });
    },
  });

  const deleteCustomMutation = useMutation({
    mutationFn: (id: number) => apiRequest('DELETE', `/api/company-service-rates/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/company-service-rates'] });
      toast({
        title: "Success",
        description: "Custom rate deleted successfully",
      });
    },
    onError: (error: any) => {
      let description = "Failed to delete custom rate";
      try {
        const raw = error?.message ?? "";
        const jsonStart = raw.indexOf("{");
        if (jsonStart !== -1) {
          const parsed = JSON.parse(raw.slice(jsonStart));
          if (parsed?.message) description = parsed.message;
        }
      } catch {}
      toast({
        title: "Error",
        description,
        variant: "destructive",
      });
    },
  });

  const handleEdit = (serviceRate: ServiceRateWithRelations) => {
    setSelectedServiceRate(serviceRate);
    setEditModalOpen(true);
  };

  const handleDelete = (id: number) => {
    setDeleteStandardId(id);
  };

  const handleEditCustom = (rate: CompanyServiceRateWithRelations) => {
    setSelectedCustomRate(rate);
    setEditCustomModalOpen(true);
  };

  const handleDeleteCustom = (id: number) => {
    setDeleteCustomId(id);
  };

  // Filter standard rates
  const filteredStandardRates = useMemo(() => {
    return serviceRates.filter((rate) => {
      // Service type filter
      if (standardServiceTypeFilter !== "all" && rate.serviceTypeId !== standardServiceTypeFilter) {
        return false;
      }
      // Status filter using derived status
      if (standardStatusFilter === "active" && rate.derivedStatus !== 'active') {
        return false;
      }
      if (standardStatusFilter === "scheduled" && rate.derivedStatus !== 'scheduled') {
        return false;
      }
      if (standardStatusFilter === "superseded" && rate.derivedStatus !== 'superseded') {
        return false;
      }
      // Date filter
      if (standardDateFilter) {
        const filterDate = fromDateOnlyString(standardDateFilter);
        const rateDate = fromDateOnlyString(rate.effectiveDate);
        if (rateDate.toDateString() !== filterDate.toDateString()) {
          return false;
        }
      }
      return true;
    }).sort((a, b) => {
      // Sort by service type name, then by effective date descending
      const typeCompare = a.serviceType.name.localeCompare(b.serviceType.name);
      if (typeCompare !== 0) return typeCompare;
      return fromDateOnlyString(b.effectiveDate).getTime() - fromDateOnlyString(a.effectiveDate).getTime();
    });
  }, [serviceRates, standardServiceTypeFilter, standardStatusFilter, standardDateFilter]);

  // Compute which standard rate IDs cannot be deleted:
  // a rate is protected when it is the only active, non-deleted rate for an active service type
  const protectedRateIds = useMemo(() => {
    const protected_ = new Set<number>();
    // Group all active, non-deleted rates by serviceTypeId
    const byServiceType = new Map<string, ServiceRateWithDerivedStatus[]>();
    for (const rate of serviceRates) {
      if (!rate.isActive || rate.deletedAt) continue;
      if (!byServiceType.has(rate.serviceTypeId)) byServiceType.set(rate.serviceTypeId, []);
      byServiceType.get(rate.serviceTypeId)!.push(rate);
    }
    for (const rates of Array.from(byServiceType.values())) {
      if (rates.length === 1 && rates[0].serviceType?.isActive) {
        protected_.add(rates[0].id);
      }
    }
    return protected_;
  }, [serviceRates]);

  // Filter custom rates
  const filteredCustomRates = useMemo(() => {
    return companyServiceRates.filter((rate) => {
      // Service type filter
      if (customServiceTypeFilter !== "all" && rate.serviceTypeId !== customServiceTypeFilter) {
        return false;
      }
      // Status filter
      if (customStatusFilter === "active" && !rate.isActive) {
        return false;
      }
      if (customStatusFilter === "inactive" && rate.isActive) {
        return false;
      }
      // Company search
      if (customCompanySearch) {
        const searchLower = customCompanySearch.toLowerCase();
        const hasMatchingCompany = rate.companies.some(
          (company) => company.name.toLowerCase().includes(searchLower)
        );
        if (!hasMatchingCompany) {
          return false;
        }
      }
      // Date filter - check if the filter date falls within the rate's effective range
      if (customDateFilter) {
        const filterDate = parseDateOnly(customDateFilter);
        const startDate = parseDateOnly(rate.effectiveStartDate);
        const endDate = rate.effectiveEndDate ? parseDateOnly(rate.effectiveEndDate) : null;
        
        if (filterDate < startDate) {
          return false;
        }
        if (endDate && filterDate > endDate) {
          return false;
        }
      }
      return true;
    }).sort((a, b) => {
      // Sort by service type name, then by effective start date descending
      const typeCompare = a.serviceType.name.localeCompare(b.serviceType.name);
      if (typeCompare !== 0) return typeCompare;
      return parseDateOnly(b.effectiveStartDate).getTime() - parseDateOnly(a.effectiveStartDate).getTime();
    });
  }, [companyServiceRates, customServiceTypeFilter, customStatusFilter, customCompanySearch, customDateFilter]);

  const formatDateRange = (startDate: string, endDate: string | null) => {
    const start = parseDateOnly(startDate).toLocaleDateString();
    if (!endDate) return `${start} - Ongoing`;
    return `${start} - ${parseDateOnly(endDate).toLocaleDateString()}`;
  };

  const clearStandardFilters = () => {
    setStandardServiceTypeFilter("all");
    setStandardStatusFilter("active");
    setStandardDateFilter("");
  };

  const clearCustomFilters = () => {
    setCustomServiceTypeFilter("all");
    setCustomStatusFilter("all");
    setCustomCompanySearch("");
    setCustomDateFilter("");
  };

  const hasStandardFilters = standardServiceTypeFilter !== "all" || standardStatusFilter !== "active" || standardDateFilter !== "";
  const hasCustomFilters = customServiceTypeFilter !== "all" || customStatusFilter !== "all" || customCompanySearch !== "" || customDateFilter !== "";

  return (
    <ResponsiveLayout 
      title="Service Rates" 
      subtitle="Manage pricing for different services with effective dates"
      currentPage="service-rates"
    >
      <div className="space-y-4">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
            <TabsList>
              <TabsTrigger value="standard" data-testid="tab-standard-rates">
                <DollarSign className="h-4 w-4 mr-2" />
                Standard Rates
              </TabsTrigger>
              <TabsTrigger value="custom" data-testid="tab-custom-rates">
                <Building2 className="h-4 w-4 mr-2" />
                Custom Rates
              </TabsTrigger>
            </TabsList>
            
            {activeTab === "standard" ? (
              <Button onClick={() => setCreateModalOpen(true)} size="sm" data-testid="button-add-standard-rate">
                <Plus className="h-4 w-4 mr-2" />
                Add Rate
              </Button>
            ) : (
              <Button onClick={() => setCreateCustomModalOpen(true)} size="sm" data-testid="button-add-custom-rate">
                <Plus className="h-4 w-4 mr-2" />
                Add Custom Rate
              </Button>
            )}
          </div>

          <TabsContent value="standard" className="space-y-4">
            {/* Standard Rates Filters */}
            <Card>
              <CardContent className="p-4">
                <div className="flex flex-wrap items-center gap-3">
                  <Filter className="h-4 w-4 text-muted-foreground" />
                  <Select value={standardServiceTypeFilter} onValueChange={setStandardServiceTypeFilter}>
                    <SelectTrigger className="w-44" data-testid="select-standard-service-type">
                      <SelectValue placeholder="Service" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Services</SelectItem>
                      {serviceTypes.map((type) => (
                        <SelectItem key={type.id} value={type.id}>
                          {type.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  
                  <Select value={standardStatusFilter} onValueChange={setStandardStatusFilter}>
                    <SelectTrigger className="w-36" data-testid="select-standard-status">
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Statuses</SelectItem>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="scheduled">Scheduled</SelectItem>
                      <SelectItem value="superseded">Inactive</SelectItem>
                    </SelectContent>
                  </Select>

                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    <Input
                      type="date"
                      value={standardDateFilter}
                      onChange={(e) => setStandardDateFilter(e.target.value)}
                      className="w-40"
                      placeholder="Effective Date"
                      data-testid="input-standard-date"
                    />
                  </div>

                  {hasStandardFilters && (
                    <Button variant="ghost" size="sm" onClick={clearStandardFilters} className="text-muted-foreground">
                      <X className="h-4 w-4 mr-1" />
                      Clear
                    </Button>
                  )}

                  <span className="text-sm text-muted-foreground ml-auto">
                    {filteredStandardRates.length} rate{filteredStandardRates.length !== 1 ? 's' : ''}
                  </span>
                </div>
              </CardContent>
            </Card>

            {/* Standard Rates Table */}
            {isLoading ? (
              <Card>
                <CardContent className="p-8 text-center">
                  <div className="animate-pulse space-y-3">
                    <div className="h-4 bg-muted rounded w-3/4 mx-auto"></div>
                    <div className="h-4 bg-muted rounded w-1/2 mx-auto"></div>
                  </div>
                </CardContent>
              </Card>
            ) : filteredStandardRates.length === 0 ? (
              <Card>
                <CardContent className="p-8 text-center">
                  <DollarSign className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-medium">No service rates found</h3>
                  <p className="text-muted-foreground mb-4">
                    {serviceRates.length > 0 
                      ? "Try adjusting your filters to see more results." 
                      : "Create your first service rate to get started."}
                  </p>
                  {serviceRates.length === 0 && (
                    <Button onClick={() => setCreateModalOpen(true)}>
                      <Plus className="h-4 w-4 mr-2" />
                      Add Service Rate
                    </Button>
                  )}
                </CardContent>
              </Card>
            ) : (
              <Card>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Service</TableHead>
                      <TableHead className="text-right">Rate</TableHead>
                      <TableHead className="text-right">Direct Cost</TableHead>
                      <TableHead>Effective Date</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="w-20"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredStandardRates.map((rate) => (
                      <TableRow key={rate.id} data-testid={`row-standard-rate-${rate.id}`}>
                        <TableCell className="font-medium">{rate.serviceType.name}</TableCell>
                        <TableCell className="text-right font-semibold text-green-600">
                          {parseFloat(rate.rate).toLocaleString('en-US', {
                            style: 'currency',
                            currency: 'USD'
                          })}
                        </TableCell>
                        <TableCell className="text-right text-muted-foreground">
                          {rate.directCost ? parseFloat(rate.directCost).toLocaleString('en-US', {
                            style: 'currency',
                            currency: 'USD'
                          }) : '-'}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {formatDateForDisplay(rate.effectiveDate)}
                        </TableCell>
                        <TableCell>
                          <Badge 
                            className={`text-xs ${
                              rate.derivedStatus === 'active' 
                                ? 'bg-green-100 text-green-800' 
                                : rate.derivedStatus === 'scheduled' 
                                  ? 'bg-blue-100 text-blue-800' 
                                  : 'bg-gray-100 text-gray-600'
                            }`}
                          >
                            {rate.derivedStatus === 'active' 
                              ? 'Active' 
                              : rate.derivedStatus === 'scheduled' 
                                ? 'Scheduled' 
                                : 'Inactive'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEdit(rate)}
                              data-testid={`button-edit-rate-${rate.id}`}
                            >
                              <Edit2 className="h-4 w-4" />
                            </Button>
                            {!protectedRateIds.has(rate.id) && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDelete(rate.id)}
                                disabled={deleteMutation.isPending}
                                data-testid={`button-delete-rate-${rate.id}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="custom" className="space-y-4">
            {/* Custom Rates Filters */}
            <Card>
              <CardContent className="p-4">
                <div className="flex flex-wrap items-center gap-3">
                  <div className="relative flex-1 min-w-48 max-w-xs">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search companies..."
                      value={customCompanySearch}
                      onChange={(e) => setCustomCompanySearch(e.target.value)}
                      className="pl-10"
                      data-testid="input-custom-company-search"
                    />
                  </div>

                  <Select value={customServiceTypeFilter} onValueChange={setCustomServiceTypeFilter}>
                    <SelectTrigger className="w-44" data-testid="select-custom-service-type">
                      <SelectValue placeholder="Service" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Services</SelectItem>
                      {serviceTypes.map((type) => (
                        <SelectItem key={type.id} value={type.id}>
                          {type.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  
                  <Select value={customStatusFilter} onValueChange={setCustomStatusFilter}>
                    <SelectTrigger className="w-32" data-testid="select-custom-status">
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Statuses</SelectItem>
                      <SelectItem value="active">Active Only</SelectItem>
                      <SelectItem value="inactive">Inactive Only</SelectItem>
                    </SelectContent>
                  </Select>

                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    <Input
                      type="date"
                      value={customDateFilter}
                      onChange={(e) => setCustomDateFilter(e.target.value)}
                      className="w-40"
                      placeholder="Effective as of"
                      data-testid="input-custom-date"
                    />
                  </div>

                  {hasCustomFilters && (
                    <Button variant="ghost" size="sm" onClick={clearCustomFilters} className="text-muted-foreground">
                      <X className="h-4 w-4 mr-1" />
                      Clear
                    </Button>
                  )}

                  <span className="text-sm text-muted-foreground ml-auto">
                    {filteredCustomRates.length} rate{filteredCustomRates.length !== 1 ? 's' : ''}
                  </span>
                </div>
              </CardContent>
            </Card>

            {/* Custom Rates Table */}
            {isLoadingCustom ? (
              <Card>
                <CardContent className="p-8 text-center">
                  <div className="animate-pulse space-y-3">
                    <div className="h-4 bg-muted rounded w-3/4 mx-auto"></div>
                    <div className="h-4 bg-muted rounded w-1/2 mx-auto"></div>
                  </div>
                </CardContent>
              </Card>
            ) : filteredCustomRates.length === 0 ? (
              <Card>
                <CardContent className="p-8 text-center">
                  <Building2 className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-medium">No custom rates found</h3>
                  <p className="text-muted-foreground mb-4">
                    {companyServiceRates.length > 0 
                      ? "Try adjusting your filters to see more results." 
                      : "Custom rates override standard rates for specific companies."}
                  </p>
                  {companyServiceRates.length === 0 && (
                    <Button onClick={() => setCreateCustomModalOpen(true)}>
                      <Plus className="h-4 w-4 mr-2" />
                      Add Custom Rate
                    </Button>
                  )}
                </CardContent>
              </Card>
            ) : (
              <Card>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Service</TableHead>
                      <TableHead className="text-right">Rate</TableHead>
                      <TableHead className="text-right">Direct Cost</TableHead>
                      <TableHead>Companies</TableHead>
                      <TableHead>Effective Period</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="w-20"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredCustomRates.map((rate) => (
                      <TableRow key={rate.id} data-testid={`row-custom-rate-${rate.id}`}>
                        <TableCell className="font-medium">{rate.serviceType.name}</TableCell>
                        <TableCell className="text-right font-semibold text-blue-600">
                          {parseFloat(rate.rate).toLocaleString('en-US', {
                            style: 'currency',
                            currency: 'USD'
                          })}
                        </TableCell>
                        <TableCell className="text-right text-muted-foreground">
                          {rate.directCost ? parseFloat(rate.directCost).toLocaleString('en-US', {
                            style: 'currency',
                            currency: 'USD'
                          }) : '-'}
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1 max-w-xs">
                            {rate.companies.slice(0, 2).map((company) => (
                              <Badge key={company.id} variant="outline" className="text-xs">
                                {company.name}
                              </Badge>
                            ))}
                            {rate.companies.length > 2 && (
                              <Badge variant="secondary" className="text-xs">
                                +{rate.companies.length - 2}
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-muted-foreground text-sm">
                          {formatDateRange(rate.effectiveStartDate, rate.effectiveEndDate)}
                        </TableCell>
                        <TableCell>
                          <Badge variant={rate.isActive ? "default" : "secondary"} className="text-xs">
                            {rate.isActive ? "Active" : "Inactive"}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEditCustom(rate)}
                              data-testid={`button-edit-custom-rate-${rate.id}`}
                            >
                              <Edit2 className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteCustom(rate.id)}
                              disabled={deleteCustomMutation.isPending}
                              data-testid={`button-delete-custom-rate-${rate.id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Card>
            )}
          </TabsContent>
        </Tabs>

        <CreateServiceRateModal
          open={createModalOpen}
          onClose={() => setCreateModalOpen(false)}
        />
        
        {selectedServiceRate && (
          <EditServiceRateModal
            open={editModalOpen}
            onClose={() => {
              setEditModalOpen(false);
              setSelectedServiceRate(null);
            }}
            serviceRate={selectedServiceRate}
          />
        )}

        <CreateCompanyServiceRateModal
          open={createCustomModalOpen}
          onClose={() => setCreateCustomModalOpen(false)}
        />

        {selectedCustomRate && (
          <EditCompanyServiceRateModal
            open={editCustomModalOpen}
            onClose={() => {
              setEditCustomModalOpen(false);
              setSelectedCustomRate(null);
            }}
            customRate={selectedCustomRate}
          />
        )}

        {/* Delete confirmation — standard rate */}
        <AlertDialog open={deleteStandardId !== null} onOpenChange={(open) => { if (!open) setDeleteStandardId(null); }}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete service rate?</AlertDialogTitle>
              <AlertDialogDescription>
                This action cannot be undone. If this is the only rate for an active service, deletion will be blocked.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => setDeleteStandardId(null)}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                onClick={() => {
                  if (deleteStandardId !== null) {
                    deleteMutation.mutate(deleteStandardId);
                    setDeleteStandardId(null);
                  }
                }}
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Delete confirmation — custom rate */}
        <AlertDialog open={deleteCustomId !== null} onOpenChange={(open) => { if (!open) setDeleteCustomId(null); }}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete custom rate?</AlertDialogTitle>
              <AlertDialogDescription>
                This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => setDeleteCustomId(null)}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                onClick={() => {
                  if (deleteCustomId !== null) {
                    deleteCustomMutation.mutate(deleteCustomId);
                    setDeleteCustomId(null);
                  }
                }}
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </ResponsiveLayout>
  );
}
